const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Fetch account managers (renamed from sales managers)
export const fetchAccountManagers = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/users`);
    if (response.ok) {
      const data = await response.json();
      const accountManagers = (data.users || []).filter(u => {
        const userRoles = u.roles || [u.role];
        const isAccountManager = userRoles.some(role => 
          role === 'account_manager' || 
          role === 'lead_account_manager'
        );
        
        // SECURITY FIX: Log and filter out non-account managers
        if (!isAccountManager) {
          console.log(`[FILTER] Excluding ${u.name} from account managers. Roles: ${userRoles}`);
        }
        
        return isAccountManager;
      });
      
      console.log(`[ACCOUNT_MANAGERS] Found ${accountManagers.length} valid account managers`);
      return accountManagers;
    }
    return [];
  } catch (error) {
    console.error('Error fetching account managers:', error);
    throw error;
  }
};

// Legacy alias for backwards compatibility
export const fetchSalesUsers = fetchAccountManagers;

export const fetchClients = async (selectedUser) => {
  try {
    // STRONG BRIDGE: Validate selectedUser has proper account manager roles
    if (!selectedUser || !selectedUser.id) {
      console.error('fetchClients called without valid selectedUser:', selectedUser);
      return [];
    }
    
    const userRoles = selectedUser.roles || [selectedUser.role];
    const isAccountManager = userRoles.some(role => 
      role === 'account_manager' || role === 'lead_account_manager'
    );
    
    // SECURITY FIX: Only account managers can see clients
    if (!isAccountManager) {
      console.log(`[SECURITY] User ${selectedUser.full_name} is not an account manager. Roles: ${userRoles}. Returning empty client list.`);
      return [];
    }
    
    const response = await fetch(`${BACKEND_URL}/api/customers`);
    if (response.ok) {
      const data = await response.json();
      let allClients = Array.isArray(data.customers) ? data.customers : [];
      
      // Filter based on user role
      if (userRoles.includes('account_manager')) {
        // Account managers see only their own clients
        allClients = allClients.filter(c => 
          c.account_manager === selectedUser.full_name ||
          c.account_manager_id === selectedUser.id ||
          c.created_by === selectedUser.id
        );
      }
      // Lead account manager sees all clients
      
      console.log(`[CLIENTS] ${selectedUser.full_name} (${userRoles.join(',')}) sees ${allClients.length} clients`);
      return allClients;
    }
    return [];
  } catch (error) {
    console.error('Error fetching clients:', error);
    return [];
  }
};

export const fetchJobs = async (selectedUser) => {
  try {
    // STRONG BRIDGE: Validate selectedUser before fetching
    if (!selectedUser || !selectedUser.id) {
      console.error('fetchJobs called without valid selectedUser:', selectedUser);
      return [];
    }
    
    const userRoles = selectedUser.roles || [selectedUser.role];
    const isAccountManager = userRoles.some(role => 
      role === 'account_manager' || role === 'lead_account_manager'
    );
    
    // SECURITY FIX: Only account managers should access jobs via this function in account manager context
    if (!isAccountManager) {
      console.log(`[SECURITY] User ${selectedUser.full_name} is not an account manager. Roles: ${userRoles}. Returning empty job list.`);
      return [];
    }
    
    const response = await fetch(`${BACKEND_URL}/api/jobs`);
    if (response.ok) {
      const data = await response.json();
      let allJobs = Array.isArray(data) ? data : [];
      
      // For Account Managers: Filter by VISIBILITY (assigned_clients)
      // Per ACCOUNT_MANAGER_BUSINESS_LOGIC.md: AMs see ALL jobs for their assigned clients
      // Performance metrics (credit) are handled separately in ActivitySummary
      if (userRoles.includes('account_manager') || userRoles.includes('lead_account_manager')) {
        const assignedClients = selectedUser.assigned_clients || [];
        
        if (assignedClients.length > 0) {
          // Filter jobs by assigned clients (visibility logic)
          allJobs = allJobs.filter(job => {
            return job.client_company && assignedClients.includes(job.client_company);
          });
        } else {
          // Fallback: If no assigned_clients, show jobs they own
          allJobs = allJobs.filter(job => {
            const isOwner = job.account_manager === selectedUser.full_name ||
                           job.account_manager_name === selectedUser.full_name;
            return isOwner;
          });
        }
      }
      
      console.log(`[JOBS] ${selectedUser.full_name} (${userRoles.join(',')}) sees ${allJobs.length} jobs`);
      return allJobs;
    }
    return [];
  } catch (error) {
    console.error('Error fetching jobs:', error);
    return [];
  }
};

export const fetchSubmissions = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/submissions-list`);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching submissions:', error);
    return [];
  }
};

export const fetchTeamData = async (selectedUser) => {
  const userRoles = selectedUser.roles || [selectedUser.role];
  if (!userRoles.includes('lead_account_manager')) {
    return { team: null, members: [] };
  }

  try {
    const teamsResponse = await fetch(`${BACKEND_URL}/api/teams`);
    if (teamsResponse.ok) {
      const teamsData = await teamsResponse.json();
      const userTeam = (teamsData.teams || []).find(t => t.team_lead_id === selectedUser.id);
      
      if (userTeam && userTeam.team_members) {
        // Fetch team member details
        const usersResponse = await fetch(`${BACKEND_URL}/api/users`);
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          const members = (usersData.users || []).filter(u => 
            userTeam.team_members.includes(u.id)
          );
          return { team: userTeam, members };
        }
      }
      
      return { team: userTeam, members: [] };
    }
    return { team: null, members: [] };
  } catch (error) {
    console.error('Error fetching team data:', error);
    return { team: null, members: [] };
  }
};
